import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "purchaseLocation",
    "vendorId",
    "vendorName",
    "storeButton",
    "secondHandButton",
    "typeahead",
    "input",
    "dropdown",
    "newBadge"
  ]

  static values = { vendors: Array }

  connect() {
    this._activeIndex = -1
    this._committed = false
    this._updatePillStyles()
    this._prepopulate()
  }

  selectStore() {
    this.purchaseLocationTarget.value = "store"
    this._updatePillStyles()
    this.typeaheadTarget.classList.remove("hidden")
    this.inputTarget.focus()
  }

  selectSecondHand() {
    this.purchaseLocationTarget.value = "second_hand"
    this._updatePillStyles()
    this.typeaheadTarget.classList.add("hidden")
    this._clearVendor()
    this.inputTarget.value = ""
    this._closeDropdown()
  }

  onInput() {
    this._committed = false
    this.vendorIdTarget.value = ""
    this.vendorNameTarget.value = ""
    this.newBadgeTarget.classList.add("hidden")

    const query = this.inputTarget.value.trim()
    if (query.length === 0) {
      this._closeDropdown()
      return
    }

    const matches = this._filter(query)
    this._renderDropdown(matches, query)
  }

  onKeydown(event) {
    const items = this.dropdownTarget.querySelectorAll("[data-role='option']")
    if (!items.length) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this._activeIndex = Math.min(this._activeIndex + 1, items.length - 1)
      this._highlightActive(items)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this._activeIndex = Math.max(this._activeIndex - 1, 0)
      this._highlightActive(items)
    } else if (event.key === "Enter") {
      event.preventDefault()
      if (this._activeIndex >= 0 && items[this._activeIndex]) {
        items[this._activeIndex].dispatchEvent(new MouseEvent("mousedown", { bubbles: true }))
      }
    } else if (event.key === "Escape") {
      this._closeDropdown()
      this.inputTarget.blur()
    }
  }

  onBlur() {
    setTimeout(() => {
      this._closeDropdown()
      if (!this._committed) {
        this.inputTarget.value = ""
        this.vendorIdTarget.value = ""
        this.vendorNameTarget.value = ""
        this.newBadgeTarget.classList.add("hidden")
      }
    }, 150)
  }

  _filter(query) {
    const q = query.toLowerCase()
    return this.vendorsValue.filter(v => v.name.toLowerCase().includes(q))
  }

  _renderDropdown(matches, query) {
    this._activeIndex = -1
    this.dropdownTarget.innerHTML = ""

    if (matches.length > 0) {
      matches.forEach(vendor => {
        const item = document.createElement("button")
        item.type = "button"
        item.dataset.role = "option"
        item.className = "w-full text-left px-4 py-2.5 text-sm text-hearth-100 hover:bg-hearth-600 transition-colors"
        item.textContent = vendor.name
        item.addEventListener("mousedown", () => {
          this.vendorIdTarget.value = vendor.id
          this.vendorNameTarget.value = ""
          this.inputTarget.value = vendor.name
          this.newBadgeTarget.classList.add("hidden")
          this._committed = true
          this._closeDropdown()
        })
        this.dropdownTarget.appendChild(item)
      })
    } else {
      const item = document.createElement("button")
      item.type = "button"
      item.dataset.role = "option"
      item.className = "w-full text-left px-4 py-2.5 text-sm text-warm-amber-400 hover:bg-hearth-600 transition-colors"
      item.innerHTML = `<span class="text-hearth-400 mr-1.5">+</span>Add "${this._esc(query)}"`
      item.addEventListener("mousedown", () => {
        this.vendorNameTarget.value = query
        this.vendorIdTarget.value = ""
        this.inputTarget.value = query
        this.newBadgeTarget.classList.remove("hidden")
        this._committed = true
        this._closeDropdown()
      })
      this.dropdownTarget.appendChild(item)
    }

    this.dropdownTarget.classList.remove("hidden")
  }

  _highlightActive(items) {
    items.forEach((item, i) => {
      item.classList.toggle("bg-hearth-600", i === this._activeIndex)
    })
  }

  _closeDropdown() {
    this.dropdownTarget.classList.add("hidden")
    this.dropdownTarget.innerHTML = ""
    this._activeIndex = -1
  }

  _clearVendor() {
    this.vendorIdTarget.value = ""
    this.vendorNameTarget.value = ""
    this.newBadgeTarget.classList.add("hidden")
    this._committed = false
  }

  _updatePillStyles() {
    const loc = this.purchaseLocationTarget.value
    this.storeButtonTarget.dataset.state = loc === "store" ? "selected" : "unselected"
    this.secondHandButtonTarget.dataset.state = loc === "second_hand" ? "selected" : "unselected"
  }

  _prepopulate() {
    if (this.purchaseLocationTarget.value !== "store") return

    const vendorId = this.vendorIdTarget.value
    const vendorName = this.vendorNameTarget.value

    if (vendorId) {
      const vendor = this.vendorsValue.find(v => v.id === vendorId)
      if (vendor) {
        this.inputTarget.value = vendor.name
        this._committed = true
      }
    } else if (vendorName) {
      this.inputTarget.value = vendorName
      this.newBadgeTarget.classList.remove("hidden")
      this._committed = true
    }
  }

  _esc(str) {
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }
}
